import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { API, axios } from "@/api";
import { BottomNav } from "@/components/shared/Navigation";
import {
  ArrowLeft, Trophy, ShieldCheck, Search, Share2, Users, Calendar,
  Flame, Gift, MapPin, Star, Target, Heart, Map, CheckCircle, MessageCircle,
  AlertTriangle, Edit, DollarSign, Award, Cake, UserCheck, Gem, Crown,
  Compass, Sprout, Loader2, Copy, ChevronRight, X, Smartphone, QrCode,
} from "lucide-react";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { useCopyBank } from "@/hooks/useCopyBank";

const ICON_MAP = {
  "search": Search, "award": Award, "shield-check": ShieldCheck,
  "alert-triangle": AlertTriangle, "edit": Edit, "dollar-sign": DollarSign,
  "check-circle": CheckCircle, "share-2": Share2, "user-check": UserCheck,
  "calendar": Calendar, "flame": Flame, "users": Users, "gift": Gift,
  "map-pin": MapPin, "star": Star, "trophy": Trophy, "cake": Cake,
  "heart": Heart, "map": Map, "target": Target, "message-circle": MessageCircle,
  "seedling": Sprout, "compass": Compass, "crown": Crown, "gem": Gem,
};

const TYPE_COLORS = {
  verification: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", icon: "text-emerald-500" },
  submission: { bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-700", icon: "text-sky-500" },
  bonus: { bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-700", icon: "text-violet-500" },
  claim: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", icon: "text-amber-500" },
  social: { bg: "bg-pink-50", border: "border-pink-200", text: "text-pink-700", icon: "text-pink-500" },
  profile: { bg: "bg-indigo-50", border: "border-indigo-200", text: "text-indigo-700", icon: "text-indigo-500" },
  streak: { bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-700", icon: "text-orange-500" },
  referral: { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", icon: "text-rose-500" },
  badge: { bg: "bg-teal-50", border: "border-teal-200", text: "text-teal-700", icon: "text-teal-500" },
  engagement: { bg: "bg-cyan-50", border: "border-cyan-200", text: "text-cyan-700", icon: "text-cyan-500" },
  quest: { bg: "bg-fuchsia-50", border: "border-fuchsia-200", text: "text-fuchsia-700", icon: "text-fuchsia-500" },
  feedback: { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-700", icon: "text-slate-500" },
};

export default function EarnPointsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getText } = useCopyBank();
  const [guide, setGuide] = useState(null);
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    const fetch = async () => {
      try {
        const [guideRes, streakRes] = await Promise.all([
          axios.get(`${API}/points/guide`),
          axios.get(`${API}/streak`),
        ]);
        setGuide(guideRes.data);
        setStreak(streakRes.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const handleCheckin = async () => {
    try {
      const res = await axios.post(`${API}/streak/checkin`);
      if (res.data.already_checked_in) {
        toast.info("Already checked in today!");
      } else {
        toast.success(`+${res.data.points_earned} pts! Day ${res.data.streak} streak`);
        if (res.data.bonus_label) toast.success(res.data.bonus_label);
      }
      setStreak({ streak: res.data.streak, checked_in_today: true });
    } catch (e) {
      toast.error("Check-in failed");
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>;

  const types = ["all", "verification", "streak", "referral", "social", "engagement", "badge"];
  const filtered = activeTab === "all" ? guide?.actions : guide?.actions?.filter(a => a.type === activeTab);

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: "var(--theme-page-bg, #ffffff)" }} data-testid="earn-points-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-amber-400 via-orange-400 to-rose-400 text-white p-4 pt-6 pb-6">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition" data-testid="earn-points-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-bold flex-1">How to Earn Points</h1>
        </div>

        {/* Score summary */}
        <div className="flex items-center gap-4">
          <div className="bg-white/20 rounded-2xl px-5 py-3 text-center">
            <p className="text-3xl font-black">{user?.crowdsourcing_score || 0}</p>
            <p className="text-xs font-medium text-white/80">Total Points</p>
          </div>
          <div className="flex-1 space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">{user?.level || "Newbie"}</span>
              {guide?.levels && (() => {
                const current = guide.levels.findIndex(l => l.name === (user?.level || "Newbie"));
                const next = guide.levels[current + 1];
                return next ? <span className="text-xs text-white/70">Next: {next.name} ({next.min_points} pts)</span> : null;
              })()}
            </div>
            {/* Progress bar */}
            {guide?.levels && (() => {
              const score = user?.crowdsourcing_score || 0;
              const current = guide.levels.findIndex(l => l.name === (user?.level || "Newbie"));
              const next = guide.levels[current + 1];
              if (!next) return <div className="h-2 bg-white/30 rounded-full"><div className="h-full bg-white rounded-full w-full" /></div>;
              const prev = guide.levels[current];
              const pct = Math.min(100, Math.round(((score - prev.min_points) / (next.min_points - prev.min_points)) * 100));
              return <div className="h-2 bg-white/30 rounded-full"><div className="h-full bg-white rounded-full transition-all" style={{ width: `${pct}%` }} /></div>;
            })()}
          </div>
        </div>
      </div>

      <div className="px-4 mt-4 space-y-4">
        {/* Daily Streak Card */}
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-2xl p-4" data-testid="streak-card">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
              <Flame className="w-6 h-6 text-orange-500" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-slate-800">{streak?.streak || 0} Day Streak</p>
              <p className="text-xs text-slate-500" data-testid="streak-prompt">
                {streak?.streak >= 30 ? "Incredible! Keep it going!" :
                 streak?.streak >= 7 ? "Great streak! 30 days = 50 pts bonus" :
                 getText("check_in")}
              </p>
            </div>
            {streak?.checked_in_today && (
              <span className="px-2.5 py-1 rounded-lg bg-emerald-100 text-emerald-600 text-[10px] font-bold" data-testid="checkin-status">Checked in</span>
            )}
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 no-scrollbar">
          {types.map(t => (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition ${
                activeTab === t ? "bg-slate-800 text-white" : "bg-white border border-slate-200 text-slate-500 hover:bg-slate-50"
              }`}
              data-testid={`tab-${t}`}
            >
              {t === "all" ? "All" : t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {/* Points Actions List */}
        <div className="space-y-2" data-testid="points-actions-list">
          {/* Earning Points section */}
          {filtered?.some(a => a.section === "earn") && (
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider pt-1" data-testid="section-earn">Earning Points</p>
          )}
          {filtered?.filter(a => a.section === "earn").map((action, i) => {
            const colors = TYPE_COLORS[action.type] || TYPE_COLORS.feedback;
            const IconComp = ICON_MAP[action.icon] || CheckCircle;
            return (
              <div key={`earn-${i}`} className={`${colors.bg} border ${colors.border} rounded-xl p-3 flex items-center gap-3`} data-testid={`action-item-earn-${i}`}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-white/60">
                  <IconComp className={`w-4.5 h-4.5 ${colors.icon}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold ${colors.text}`}>{action.action}</p>
                  <p className="text-xs text-slate-500 leading-snug">{action.description}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className={`text-sm font-black ${colors.text}`}>{typeof action.points === "number" ? `+${action.points}` : action.points}</p>
                  <p className="text-[9px] text-slate-400 uppercase">pts</p>
                </div>
              </div>
            );
          })}

          {/* Bonuses section */}
          {filtered?.some(a => a.section === "bonus") && (
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider pt-3" data-testid="section-bonus">Bonuses</p>
          )}
          {filtered?.filter(a => a.section === "bonus").map((action, i) => {
            const colors = TYPE_COLORS[action.type] || TYPE_COLORS.feedback;
            const IconComp = ICON_MAP[action.icon] || CheckCircle;
            return (
              <div key={`bonus-${i}`} className={`${colors.bg} border ${colors.border} rounded-xl p-3 flex items-center gap-3`} data-testid={`action-item-bonus-${i}`}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-white/60">
                  <IconComp className={`w-4.5 h-4.5 ${colors.icon}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold ${colors.text}`}>{action.action}</p>
                  <p className="text-xs text-slate-500 leading-snug">{action.description}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className={`text-sm font-black ${colors.text}`}>{typeof action.points === "number" ? `+${action.points}` : action.points}</p>
                  <p className="text-[9px] text-slate-400 uppercase">pts</p>
                </div>
              </div>
            );
          })}

          {/* Fallback for actions without section (backwards compat) */}
          {filtered?.filter(a => !a.section).map((action, i) => {
            const colors = TYPE_COLORS[action.type] || TYPE_COLORS.feedback;
            const IconComp = ICON_MAP[action.icon] || CheckCircle;
            return (
              <div key={`other-${i}`} className={`${colors.bg} border ${colors.border} rounded-xl p-3 flex items-center gap-3`} data-testid={`action-item-${i}`}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-white/60">
                  <IconComp className={`w-4.5 h-4.5 ${colors.icon}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold ${colors.text}`}>{action.action}</p>
                  <p className="text-xs text-slate-500 leading-snug">{action.description}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className={`text-sm font-black ${colors.text}`}>{typeof action.points === "number" ? `+${action.points}` : action.points}</p>
                  <p className="text-[9px] text-slate-400 uppercase">pts</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Levels */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4" data-testid="levels-card">
          <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" />Levels</h3>
          <div className="space-y-2">
            {guide?.levels?.map((level, i) => {
              const isActive = level.name === (user?.level || "Newbie");
              const isPast = (user?.crowdsourcing_score || 0) >= level.min_points;
              const LevelIcon = ICON_MAP[level.icon] || Star;
              return (
                <div key={i} className={`flex items-center gap-3 p-2 rounded-xl ${isActive ? "bg-amber-50 border border-amber-200" : ""}`}>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isPast ? "bg-amber-100" : "bg-slate-100"}`}>
                    <LevelIcon className={`w-4 h-4 ${isPast ? "text-amber-500" : "text-slate-400"}`} />
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-semibold ${isActive ? "text-amber-700" : isPast ? "text-slate-700" : "text-slate-400"}`}>
                      {level.name} {isActive && <span className="text-xs font-normal">(You)</span>}
                    </p>
                  </div>
                  <span className={`text-xs font-medium ${isPast ? "text-amber-600" : "text-slate-400"}`}>{level.min_points} pts</span>
                </div>
              );
            })}
          </div>
        </div>

      </div>

      <BottomNav />
    </div>
  );
}
